import React from 'react';
import { Package, Shield, Box, FileText, Check, X, HelpCircle } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';

const CHECKS = [
  {
    type: 'quantity',
    title: 'Quantité reçue',
    description: 'La quantité livrée correspond à celle indiquée sur le bon.',
    Icon: Package,
  },
  {
    type: 'general_state',
    title: 'État général',
    description: 'Les marchandises sont en bon état (ni avariées ni endommagées).',
    Icon: Shield,
  },
  {
    type: 'packaging',
    title: 'Emballage intact',
    description: 'Les emballages sont intacts et non endommagés.',
    Icon: Box,
  },
  {
    type: 'order_match',
    title: 'Correspondance avec la commande',
    description: 'Les références et spécifications correspondent à la commande.',
    Icon: FileText,
  },
];

const STATUSES = [
  { value: 'conform', label: 'Conforme', color: 'emerald', Icon: Check },
  { value: 'non_conform', label: 'Non conforme', color: 'red', Icon: X },
  { value: 'not_verifiable', label: 'Non vérifiable', color: 'slate', Icon: HelpCircle },
];

const StatusButton = ({ status, currentValue, onClick, testid }) => {
  const isActive = currentValue === status.value;
  const colorMap = {
    emerald: isActive
      ? 'bg-emerald-50 border-emerald-500 text-emerald-700'
      : 'bg-white border-slate-200 text-slate-600 hover:border-emerald-300',
    red: isActive
      ? 'bg-red-50 border-red-500 text-red-700'
      : 'bg-white border-slate-200 text-slate-600 hover:border-red-300',
    slate: isActive
      ? 'bg-slate-100 border-slate-500 text-slate-700'
      : 'bg-white border-slate-200 text-slate-600 hover:border-slate-400',
  };
  const iconColor = isActive ? `text-${status.color}-600` : 'text-slate-400';
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-2 px-3 py-2 rounded-md border-2 transition-all text-sm font-medium min-w-[120px] ${colorMap[status.color]}`}
    >
      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
        isActive
          ? `bg-${status.color}-500 border-${status.color}-500`
          : 'border-slate-300'
      }`}>
        {isActive && <Check className="w-2.5 h-2.5 text-white" />}
      </div>
      <span>{status.label}</span>
    </button>
  );
};

/**
 * Liste de contrôles marchandise avec radios "Conforme / Non conforme / Non vérifiable".
 * PM Spec UI 05/05/2026 — Étape 2 du wizard de déchargement.
 */
const MerchandiseChecksList = ({ checks = [], onChange, reserveDescription = '', onReserveChange }) => {
  // Map type → status pour affichage rapide
  const checkMap = Object.fromEntries(checks.map(c => [c.type, c]));
  const completedCount = CHECKS.filter(c => checkMap[c.type]?.status).length;
  const hasNonConformity = checks.some(c => ['non_conform', 'not_verifiable'].includes(c.status));
  const allConform = completedCount === CHECKS.length && !hasNonConformity;

  const setCheck = (type, status) => {
    const others = checks.filter(c => c.type !== type);
    onChange([...others, { type, status }]);
  };

  return (
    <div className="space-y-4" data-testid="merchandise-checks-list">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-base font-semibold text-slate-800">Contrôle de la marchandise</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Contrôlez les éléments ci-dessous et signalez toute anomalie constatée.
          </p>
        </div>
        <Badge className={`text-xs font-medium ${
          completedCount === CHECKS.length
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : 'bg-orange-50 text-orange-700 border-orange-200'
        }`}>
          {completedCount} / {CHECKS.length} contrôles effectués
        </Badge>
      </div>

      {/* Tableau des contrôles */}
      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
        <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wide">
          <div className="col-span-12 sm:col-span-6">Élément à contrôler</div>
          <div className="hidden sm:block col-span-6">Statut</div>
        </div>
        {CHECKS.map(({ type, title, description, Icon }) => {
          const current = checkMap[type];
          return (
            <div key={type} className="grid grid-cols-1 sm:grid-cols-12 gap-3 px-4 py-3 border-b border-slate-100 last:border-b-0 items-center">
              <div className="sm:col-span-6 flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-emerald-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800">{title}</div>
                  <div className="text-xs text-slate-500">{description}</div>
                </div>
              </div>
              <div className="sm:col-span-6 flex flex-wrap gap-2"
                   data-testid={`check-${type}-controls`}>
                {STATUSES.map(s => (
                  <StatusButton
                    key={s.value}
                    status={s}
                    currentValue={current?.status}
                    onClick={() => setCheck(type, s.value)}
                    testid={`check-${type}-${s.value}`}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Bloc anomalie / réserve */}
      <div className={`rounded-lg p-4 border-2 ${hasNonConformity ? 'border-orange-300 bg-orange-50/40' : 'border-slate-200 bg-slate-50/40'}`}
           data-testid="reserve-block">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className={`w-5 h-5 rounded-full flex items-center justify-center ${hasNonConformity ? 'bg-orange-100 text-orange-600' : 'bg-emerald-100 text-emerald-600'}`}>
              <span className="text-sm">{hasNonConformity ? '!' : '✓'}</span>
            </div>
            <span className={`text-sm font-semibold ${hasNonConformity ? 'text-orange-700' : 'text-emerald-700'}`}>
              {hasNonConformity ? 'Anomalie ou réserve' : 'Contrôle marchandise'}
            </span>
          </div>
          {!hasNonConformity && allConform && (
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">
              <Check className="w-3 h-3 mr-1" /> Aucune réserve signalée
            </Badge>
          )}
        </div>
        {hasNonConformity ? (
          <>
            <p className="text-xs text-slate-600 mb-2">
              Si vous constatez un problème, décrivez-le ci-dessous (marchandise manquante, endommagée, refusée, non conforme, etc.).
            </p>
            <Label className="sr-only">Description anomalie</Label>
            <Textarea
              value={reserveDescription}
              onChange={(e) => onReserveChange(e.target.value.slice(0, 1000))}
              placeholder="Décrivez l'anomalie ou la réserve constatée"
              rows={5}
              className="text-sm bg-white"
              data-testid="reserve-description-input"
            />
            <div className="text-[10px] text-slate-400 text-right mt-1">
              {reserveDescription.length} / 1000
            </div>
          </>
        ) : (
          <p className="text-xs text-slate-500 italic">
            Marquez chaque contrôle comme "Conforme", "Non conforme" ou "Non vérifiable".
            Si une non-conformité est signalée, vous devrez décrire la réserve.
          </p>
        )}
      </div>
    </div>
  );
};

export default MerchandiseChecksList;
